import { zodResolver } from "@hookform/resolvers/zod";
import { type FC, useEffect, useState } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import { DistributionMethod } from "@/api/admin/fundings";
import { type BrandUnderGroup } from "@/api/admin/groups";
import type { CurrencyOptionItem } from "@/api/admin/types";
import {
  CustomCardBody,
  DetailsTitle,
  ErrorForm,
  NotFoundGroup,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  TableHeaderCustom,
  TableOver,
} from "@/components/atoms";
import { InputControl } from "@/components/molecules";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TableBody, TableRow } from "@/components/ui/table";
import { PATHS } from "@/config/path";
import { cn } from "@/lib/utils";
import type { FormProps } from "@/types/common";

import FundingCreditFields from "./FundingCreditFields";
import { type FundingFormType, FundingSchema } from "./types";

const radioGroup = [
  { title: "No limitation", value: DistributionMethod.NO_LIMITS },
  { title: "Equal parts", value: DistributionMethod.EQUAL_PARTS },
  { title: "Manual", value: DistributionMethod.MANUAL },
];

const EPSILON = 0.01;

type FundingFormProps = Omit<FormProps<FundingFormType>, "defaultValues"> & {
  defaultValues: Partial<FundingFormType>;
  /**
   * The currency this group is funded in. Carries the symbol with it, so the
   * form never has to work out how to draw an amount.
   */
  currency?: CurrencyOptionItem | null;
  activeBrands: BrandUnderGroup[];
};

const FundingForm: FC<FundingFormProps> = ({
  defaultValues,
  onSubmit,
  onValidityChange,
  id,
  classes,
  currency,
  activeBrands,
}) => {
  const [radioValue, setRadioValue] = useState(
    defaultValues.distributionMethod
  );

  const form = useForm<FundingFormType>({
    resolver: zodResolver(FundingSchema) as Resolver<FundingFormType>,
    defaultValues,
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setError,
    clearErrors,
    setValue,
  } = form;

  const handleFormSubmit = (data: FundingFormType) => {
    if (radioValue !== DistributionMethod.NO_LIMITS) {
      const totalPercent = (data.distributions ?? []).reduce(
        (sum, brand) => sum + (brand.percent ?? 0),
        0
      );

      const normalizedTotal = Math.round(totalPercent * 100) / 100;
      const diff = 100 - normalizedTotal;

      if (Math.abs(diff - 0.01) < EPSILON) {
        const lastIndex = (data.distributions?.length ?? 0) - 1;
        if (lastIndex >= 0) {
          data.distributions![lastIndex].percent =
            (data.distributions![lastIndex].percent ?? 0) + 0.01;
        }
      } else if (
        normalizedTotal < 100 - EPSILON ||
        normalizedTotal > 100 + EPSILON
      ) {
        setError("distributions", {
          type: DistributionMethod.MANUAL,
          message: "Distribution must total 100%",
        });
        return;
      }
    }

    clearErrors("distributions");
    const timezoneOffsetMinutes = -new Date().getTimezoneOffset();

    onSubmit?.({
      ...data,
      timezoneOffsetMinutes,
    });
  };

  useEffect(() => {
    if (radioValue === DistributionMethod.MANUAL) {
      clearErrors("distributions");
    }
  }, [form.watch("distributions"), radioValue]);

  useEffect(() => {
    if (form.formState.errors.distributions) {
      clearErrors("distributions");
    }
  }, [form.watch("distributions")]);

  // Calculate and set equal parts when EQUAL_PARTS is selected
  useEffect(() => {
    if (
      radioValue === DistributionMethod.EQUAL_PARTS &&
      activeBrands.length > 0
    ) {
      const basePercent = 100 / activeBrands.length;
      const roundedPercent = Math.round(basePercent * 100) / 100;

      const distributions = activeBrands.map((brand, index) => {
        // Adjust the last item to ensure total equals 100
        if (index === activeBrands.length - 1) {
          const previousTotal = roundedPercent * (activeBrands.length - 1);
          const lastPercent = Math.round((100 - previousTotal) * 100) / 100;
          return {
            brandId: brand.brandId,
            percent: lastPercent,
          };
        }
        return {
          brandId: brand.brandId,
          percent: roundedPercent,
        };
      });

      setValue("distributions", distributions, {
        shouldValidate: true,
        shouldDirty: true,
      });
      clearErrors("distributions");
    }
  }, [radioValue, activeBrands, setValue, clearErrors]);

  const isFormValid = isValid && activeBrands.length > 0;

  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, activeBrands.length, onValidityChange]);

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(handleFormSubmit)} id={id}>
        <CustomCardBody
          classes={cn("flex flex-col gap-3 px-4 md:px-8", classes)}
        >
          <div className="flex flex-col gap-5">
            <DetailsTitle title="Credit details" />
            <FundingCreditFields currency={currency} />
          </div>

          <div className="flex flex-col gap-9">
            <div className="flex flex-col gap-5.5">
              <DetailsTitle title="Credit distribution" />
              <FormField
                control={form.control}
                name="distributionMethod"
                render={() => (
                  <FormItem
                    className="space-y-3"
                    data-testid="funding-form-distribution-options"
                  >
                    <FormLabel className="mb-2">
                      Choose how to distribute credit between brands:
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={value => {
                          setRadioValue(value as DistributionMethod);
                          form.setValue(
                            "distributionMethod",
                            value as DistributionMethod,
                            { shouldValidate: true, shouldDirty: true }
                          );
                        }}
                        value={radioValue}
                        defaultValue={radioValue}
                        className="flex flex-col gap-3"
                        data-testid="funding-form-distribution-radio-group"
                      >
                        {radioGroup.map(elem => (
                          <FormItem
                            key={elem.value}
                            className="flex items-center gap-[8px]"
                          >
                            <FormControl>
                              <RadioGroupItem
                                value={elem.value}
                                className="w-[20px] h-[20px]"
                                data-testid={`funding-form-radio-input-${elem.value}`}
                                disabled={
                                  elem.value !== DistributionMethod.NO_LIMITS
                                }
                              />
                            </FormControl>
                            <FormLabel
                              className={cn(
                                "font-normal",
                                elem.value !== DistributionMethod.NO_LIMITS &&
                                  "opacity-[50%]"
                              )}
                            >
                              {elem.title}
                              {elem.value === DistributionMethod.NO_LIMITS && (
                                <span className="text-[#9CA3AF] pl-1">
                                  (all brands can consume any credit amount)
                                </span>
                              )}
                            </FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {activeBrands.length === 0 && <NotFoundGroup />}

            {activeBrands.length > 0 && (
              <TableOver>
                <>
                  <TableHeaderCustom>
                    <TableRow>
                      {["Brand name", "Credit (%)"].map(elem => (
                        <TableHeadCellCustom key={elem}>
                          {elem}
                        </TableHeadCellCustom>
                      ))}
                    </TableRow>
                  </TableHeaderCustom>
                  <TableBody>
                    {activeBrands?.map((data, index) => {
                      const isLast = index + 1 < activeBrands.length;
                      return (
                        <TableRow key={index}>
                          <TableCellLink classes="w-[50%]" last={isLast}>
                            <Link
                              to={PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(
                                data.brandId
                              )}
                              data-testid={`funding-form-table-brand-${data?.brandId}`}
                            >
                              {data?.brandName}
                            </Link>
                          </TableCellLink>
                          <TableCellText classes="w-[50%]" last={isLast}>
                            <InputControl<FundingFormType>
                              formControl={control}
                              nameField={`distributions.${index}.percent`}
                              classedName="max-w-[160px] mb-0"
                              disabled={
                                radioValue !== DistributionMethod.NO_LIMITS
                              }
                              data-testid={`funding-form-credit-input-${data?.brandId}`}
                              isPercent
                              errorClasses="top-4! left-42! h-fit"
                            />
                          </TableCellText>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              </TableOver>
            )}
            {form.formState.errors.distributions?.message?.length && (
              <ErrorForm
                message={form.formState.errors.distributions.message!}
              />
            )}
          </div>
        </CustomCardBody>
      </form>
    </Form>
  );
};

export default FundingForm;
